import Link from 'next/link';
import { NodeResort } from '@/utils/drupal-api/__generated__/graphql';
import { ImageSrcSetSize } from '@/utils/image';
import { Resort } from '@/utils/platform-api/__generated__/graphql';
import clsx from 'clsx';
import { BrandConfig } from 'types/brand-config';

import DriveImage from '@/components/ui/drive-image';
import MapIcon from '@/components/icon/map-icon';

export interface ResortCardProps {
  resort: Resort;
  cmsResort: NodeResort;
  className?: string;
  imageSizes?: ImageSrcSetSize[];
  imageAspect?: number;
  brand: BrandConfig;
  showResortName?: boolean;
}

export const ResortCard: React.FC<ResortCardProps> = ({
  resort,
  cmsResort,
  className,
  imageSizes,
  imageAspect,
  showResortName = true,
}) => {
  return (
    <div
      className={clsx(
        'group relative mb-4 w-full border-b border-b-gray-3 pb-4 last:border-b-0 last:pb-0',
        'md:mb-0 md:aspect-square md:overflow-hidden md:border-b-0 md:pb-0',
        className
      )}
      key={resort.id}
    >
      <Link
        href={cmsResort.path || ''}
        className="flex items-center gap-3 group-hover:no-underline"
      >
        {resort.images?.[0] && (
          <DriveImage
            image={resort.images?.[0]}
            alt={resort.images?.[0].caption || ''}
            loading="lazy"
            srcsetSizes={imageSizes}
            aspect={imageAspect}
            className={clsx(
              'h-28 w-28 rounded-lg object-cover',
              'md:relative md:h-full md:w-full md:rounded-none md:transition-transform md:duration-300 md:will-change-transform md:group-hover:scale-[1.04]'
            )}
          />
        )}
        <div
          className={clsx(
            'hidden',
            'md:absolute md:inset-x-0 md:bottom-0 md:z-10 md:block md:h-2/5 md:w-full md:bg-gradient-to-t md:from-primary-main md:to-transparent md:opacity-70 md:transition-opacity md:duration-300 md:group-hover:opacity-100'
          )}
        />

        {showResortName && (
          <div
            className={clsx(
              'flex flex-col-reverse gap-2',
              'md:absolute md:inset-x-0 md:bottom-0 md:z-20 md:block md:w-full md:p-8 md:text-center'
            )}
          >
            <h2 className="h5 text-primary-main md:text-white md:drop-shadow-md">
              {resort.name}
            </h2>
            {resort.locale_name && (
              <h4 className="ut-sm md:ut-md font-normal">
                <span className="flex items-center text-primary-main md:block md:text-white md:drop-shadow-md">
                  <MapIcon className="mr-1 h-4 w-4 md:hidden" />
                  {resort.locale_name}
                </span>
              </h4>
            )}
          </div>
        )}
      </Link>
    </div>
  );
};

export default ResortCard;
